/**
 * Die drei Listen unter dem Diagrammteil: Buchungen, Belege und laufende Verträge.
 *
 * <p>Alle drei standen bis Sprint 55 als Zeile mit festen Spaltenbreiten (`w-32`, `w-28`) da. Auf
 * 360 px lag der Betrag dadurch ausserhalb der Karte — die eine Zahl, wegen der man hinsieht.
 * Jetzt eine Komposition, die unter `sm` stapelt.</p>
 */
import { Pill } from '@/shared/components';
import { formatDate, formatMoney } from '@/shared/lib/format';
import { formatAccommodationPrice } from '@/shared/lib/bookingPrice';
import type { BookingResponse, ExpenseListItem, RecurringCostLine } from '@/shared/api';
// Eine Übersetzung für Buchungszustände, nicht zwei: die Buchungsliste führt sie, und ein
// zweiter Satz deutscher Labels würde beim nächsten neuen Status auseinanderlaufen.
import { statusLabels, statusVariants } from '@/features/bookings/components/bookingsFormat';
import { expenseCategoryLabel } from '../expenseCategoryLabels';
import { channelColor, channelLabel } from './financeChartTheme';

const ROW =
  'border-line/40 flex flex-col gap-1.5 border-b py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-3';
const AMOUNT = 'text-ink shrink-0 font-mono text-sm tabular-nums sm:w-28 sm:text-right';

function DrilldownEmpty({ children }: { children: React.ReactNode }) {
  return <p className="text-ink-soft py-8 text-center text-sm">{children}</p>;
}

export function BookingsList({ bookings }: { bookings: BookingResponse[] }) {
  if (bookings.length === 0) {
    return <DrilldownEmpty>In diesem Zeitraum liegt keine Buchung.</DrilldownEmpty>;
  }
  const sorted = [...bookings].sort((a, b) => b.checkInDate.localeCompare(a.checkInDate));

  return (
    <ul className="text-sm">
      {sorted.map((booking) => {
        const channel = booking.source === 'Manuell' ? 'Manuell' : (booking.channel ?? 'Unknown');
        return (
          <li key={booking.id} className={ROW}>
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <span
                aria-hidden
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: channelColor(channel) }}
              />
              <span className="text-ink truncate font-medium">{channelLabel(channel)}</span>
            </span>
            <span className="text-ink-soft shrink-0 text-xs tabular-nums sm:w-48">
              {formatDate(booking.checkInDate)} – {formatDate(booking.checkOutDate)}
            </span>
            <span className="flex items-center justify-between gap-3 sm:justify-end">
              <Pill variant={statusVariants[booking.status]} size="sm">
                {statusLabels[booking.status]}
              </Pill>
              <span className={AMOUNT}>{formatAccommodationPrice(booking)}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function ExpensesList({ expenses }: { expenses: ExpenseListItem[] }) {
  if (expenses.length === 0) {
    return <DrilldownEmpty>In diesem Zeitraum ist kein Beleg abgelegt.</DrilldownEmpty>;
  }

  return (
    <ul className="text-sm">
      {expenses.map((expense) => (
        <li key={expense.fileId} className={ROW}>
          <span className="text-ink min-w-0 flex-1 truncate font-medium">
            {expense.counterparty ?? 'Kein Aussteller hinterlegt'}
          </span>
          <span className="text-ink-soft shrink-0 text-xs tabular-nums sm:w-24">
            {formatDate(expense.issuedOn)}
          </span>
          <span className="flex items-center justify-between gap-3 sm:justify-end">
            {expense.category ? (
              <Pill variant="default" size="sm">
                {expenseCategoryLabel(expense.category)}
              </Pill>
            ) : (
              <span className="text-ink-faint text-xs">ohne Kategorie</span>
            )}
            <span className={AMOUNT}>{formatMoney(expense.amount, expense.currency)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function RecurringList({
  lines,
  currency,
}: {
  lines: RecurringCostLine[];
  currency: string;
}) {
  if (lines.length === 0) {
    return <DrilldownEmpty>In diesem Zeitraum läuft kein Vertrag mit Kosten.</DrilldownEmpty>;
  }

  return (
    <ul className="text-sm">
      {lines.map((line, index) => (
        <li key={`${line.contractId}-${line.month}-${index}`} className={ROW}>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-ink truncate font-medium">{line.counterparty}</span>
            <span className="text-ink-soft truncate text-xs">{line.type}</span>
          </span>
          <span className="text-ink-soft shrink-0 text-xs tabular-nums sm:w-24">
            {formatMonthYear(line.month)}
          </span>
          <span className="flex items-center justify-between gap-3 sm:justify-end">
            {line.replacedByInvoice && (
              <Pill variant="warning" size="sm">
                durch Beleg ersetzt
              </Pill>
            )}
            <span className={AMOUNT}>
              {line.amount === 0 ? (
                <span className="text-ink-faint">kein Betrag</span>
              ) : (
                formatMoney(line.amount, currency)
              )}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function formatMonthYear(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' });
}
